if ( ! window.ProductARLauncher ) {
  window.ProductARLauncher = {
    modelViewerUrl: 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js',
    modelViewerLoading: null,
    configuratorScriptUrl: 'https://web-packages.londondynamics.com/webcomponent/v/latest/configurator.js',
    configuratorScriptLoading: null,
    containerLoads: new WeakMap(),
    // Element-level, not per-observe()-call - confirmed live 2026-09-29 that
    // the gallery can briefly have two distinct (both isConnected) elements
    // matching the AR container selector during initial load, each
    // triggering its own observe() call. boundButtons/boundConfigurators
    // used to live as closures local to each observe() call, so two such
    // calls each got their own fresh WeakSet and neither knew about the
    // other's bind - the same button that both calls happened to agree on
    // (root cause of the duplicate-container overlap not fully pinned down)
    // ended up with more than one click listener, so a single click fired
    // getCurrentQR/toggleQR multiple times - opening the QR panel and
    // immediately closing it again, invisibly fast. Sharing these two
    // WeakSets across every observe() call makes binding idempotent per
    // element regardless of how many times observe() runs on it.
    boundButtons: new WeakSet(),
    boundConfigurators: new WeakSet(),
    rulesOnlyBound: new WeakSet(),

    init() {
      // The AR container itself isn't always present yet at defer-script
      // execution time — confirmed live that the gallery's <css-slider>
      // (component-slider.js) can still be mounting/rebuilding the gallery
      // at this point, so a single querySelectorAll here can find nothing
      // and nothing ever binds. A document-level observer catches the
      // container being inserted later, on top of the per-container observer
      // below that catches its *contents* being replaced afterwards.
      const discovered = new WeakSet();
      const discover = () => {
        document.querySelectorAll('[data-product-media-type="ar-configurator"]').forEach((container) => {
          if ( discovered.has(container) ) return;
          discovered.add(container);
          this.observe(container);
        });
      };
      discover();
      new MutationObserver(discover).observe(document.body, { childList: true, subtree: true });
    },
    loadModelViewer() {
      if ( customElements.get('model-viewer') ) return Promise.resolve();
      if ( !this.modelViewerLoading ) {
        this.modelViewerLoading = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.type = 'module';
          script.src = this.modelViewerUrl;
          script.onload = () => resolve();
          script.onerror = reject;
          document.head.appendChild(script);
        }).then(() => customElements.whenDefined('model-viewer'));
      }
      return this.modelViewerLoading;
    },
    // Same lazy-script pattern as loadModelViewer above, for London
    // Dynamics' own SDK — nothing under this domain is fetched until
    // something actually calls this (see loadAndBind below), which only
    // happens on a shopper clicking the "3D" or AR/QR button.
    loadConfiguratorScript() {
      if ( customElements.get('ld-configurator') ) return Promise.resolve();
      if ( !this.configuratorScriptLoading ) {
        this.configuratorScriptLoading = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.type = 'module';
          script.src = this.configuratorScriptUrl;
          script.onload = () => resolve();
          script.onerror = reject;
          document.head.appendChild(script);
        }).then(() => customElements.whenDefined('ld-configurator'));
      }
      return this.configuratorScriptLoading;
    },

    // The gallery's <css-slider> wrapper rebuilds its children from an
    // innerHTML string when it mounts (component-slider.js, initSlider()),
    // which can happen after this file has already run and bound to the
    // original elements, silently replacing them. A MutationObserver
    // re-binds to whatever the current elements are whenever the
    // container's contents change, instead of assuming a single bind at
    // load time is enough. Confirmed live this rebuild happens well under a
    // second after load, long before a shopper could plausibly have clicked
    // anything yet, but it's still re-checked on every mutation rather than
    // assumed to happen exactly once.
    observe(container) {
      const tryBind = () => {
        // Eager fallback: product-media-ar.liquid only renders a live
        // <ld-configurator> straight away when it had no photo to show
        // instead (see that file's `photo` param) — everything loads
        // immediately here, same as this integration always worked before
        // the photo/3D-toggle pattern existed.
        //
        // Branches on the PHOTO element's presence, not the configurator's -
        // confirmed live 2026-09-29 that branching on the configurator let
        // this misfire on the lazy path: loadAndBind's own
        // container.appendChild(configurator) is itself a mutation this same
        // per-container observer reacts to, and by the time tryBind reran,
        // a configurator now genuinely existed (ours, just created) - so
        // this treated it as the eager case and called bindEager a second
        // time on the same button already bound by bindLazyButton, stacking
        // its own unguarded toggleQR() click handler on top of the fixed
        // one. A single click then fired toggleQR() twice - opening the QR
        // panel and immediately closing it again, invisibly fast. The photo
        // element's presence is a stable, render-time marker of which mode
        // a container is in and never changes later the way the
        // configurator's presence does.
        const photo = container.querySelector('[data-ar-photo]');
        if ( !photo ) {
          const configurator = container.querySelector('[data-ar-configurator]');
          const button = container.querySelector('[data-ar-launch-button]');
          const viewer = container.querySelector('[data-ar-launch-viewer]');
          const loading = container.querySelector('[data-ar-loading]');
          if ( !configurator || !button || !viewer || this.boundConfigurators.has(configurator) ) return;
          this.boundConfigurators.add(configurator);
          this.loadModelViewer().then(() => this.bindEager(configurator, button, viewer, container, loading));
          return;
        }

        // Lazy path: nothing loads until one of these is actually clicked.
        //
        // The drawer's cross-feature rules (e.g. hiding the Phone & Tablet
        // Arm unless the S4 monitor is selected) don't need the scene itself,
        // only the sku, so run them straight away with a no-op sender instead
        // of waiting for the first 3D/AR click. loadAndBind later does its own
        // real bindVariantSync; the two just compute the same rules.
        if ( !this.rulesOnlyBound.has(container) && container.dataset.arSku ) {
          this.rulesOnlyBound.add(container);
          const skuOnly = { getAttribute: (name) => (name === 'sku' ? container.dataset.arSku : null) };
          this.bindVariantSync(skuOnly, container, () => {});
        }
        const button = container.querySelector('[data-ar-launch-button]');
        const threeDButton = container.querySelector('[data-ar-3d-button]');
        const loading = container.querySelector('[data-ar-loading]');
        [button, threeDButton].forEach((btn) => {
          if ( !btn || this.boundButtons.has(btn) ) return;
          this.boundButtons.add(btn);
          this.bindLazyButton(btn, btn === threeDButton, container, photo, loading);
        });
        this.maybeAutoEngage(container, photo, loading);
      };
      tryBind();
      new MutationObserver(tryBind).observe(container, { childList: true, subtree: true });
    },

    // Same hand-off as breadcrumb.liquid's back-link restore, for a
    // combined-listing swatch change (Wood Type) — component-product-form.js
    // (#remember3DEngaged) leaves this behind before navigating away, only
    // when the shopper had already opened the 3D view on the page they're
    // leaving. Consumed once (removed immediately) so a slider rebuild
    // re-running tryBind above can't trigger this twice, and it's read fresh
    // from sessionStorage on every call rather than cached, since it's
    // effectively a one-shot flag that either matches this exact page load
    // or doesn't.
    //
    // Deliberately delayed rather than acted on the instant tryBind sees a
    // match: the gallery's <css-slider> doesn't just mutate this container's
    // children on its early rebuild (confirmed live well under a second
    // after load) — it discards the container itself for a fresh one, which
    // init()'s document-level observer then discovers and binds
    // independently. tryBind (and so this) genuinely runs once per
    // generation, and the flag must not be consumed by whichever one is
    // about to be thrown away — confirmed live that consuming it immediately
    // let the doomed first generation win the race, leaving the real,
    // surviving container with nothing left to act on: photo hidden (this
    // method's own doing) with no scene ever created to replace it, since
    // container.appendChild in loadAndBind was inserting into an already-
    // disconnected node. Waiting and re-checking isConnected before
    // consuming anything means only whichever generation actually survives
    // gets to act — a real click never has this problem since it can't
    // physically land before the rebuild has long settled.
    maybeAutoEngage(container, photo, loading) {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem('pdp-3d-engaged'));
      } catch (e) {}
      if ( !saved || saved.target !== location.pathname || Date.now() - saved.at >= 1800000 ) return;

      setTimeout(() => {
        if ( !container.isConnected ) return; // not this generation - the surviving container's own tryBind call handles it instead

        // Re-check (rather than trust the closure's `saved`) in case another
        // generation's own delayed check already consumed it in the
        // meantime — only one of these calls should ever actually load.
        let stillSaved = null;
        try {
          stillSaved = JSON.parse(sessionStorage.getItem('pdp-3d-engaged'));
        } catch (e) {}
        if ( !stillSaved || stillSaved.at !== saved.at ) return;
        sessionStorage.removeItem('pdp-3d-engaged');

        const currentPhoto = container.querySelector('[data-ar-photo]') || photo;
        const currentLoading = container.querySelector('[data-ar-loading]') || loading;
        this.ensureLoaded(container, currentPhoto, currentLoading);
      }, 1500);
    },

    // One shared load promise per container, however many times a shopper
    // clicks either button — first click (3D or AR/QR, whichever comes
    // first) starts it, every later click on either button reuses the same
    // in-flight or already-resolved result instead of loading twice.
    ensureLoaded(container, photo, loading) {
      if ( !this.containerLoads.has(container) ) {
        this.containerLoads.set(container, this.loadAndBind(container, photo, loading).catch((error) => {
          // Load failed (script fetch error, etc.) — restore the photo and
          // uncover the spinner so a failure doesn't leave a blank gap with
          // neither showing, and let a later click retry from scratch
          // instead of staying stuck on a rejected result forever.
          if (photo) photo.hidden = false;
          if (loading) loading.classList.add('product-gallery-item__ar-loading--hidden');
          this.containerLoads.delete(container);
          return null;
        }));
      }
      return this.containerLoads.get(container);
    },

    bindLazyButton(btn, isThreeD, container, photo, loading) {
      if ( isThreeD ) {
        const defaultLabel = btn.textContent;
        btn.addEventListener('click', async () => {
          // Already showing the scene - this click exits back to the photo
          // instead of reloading anything. The scene stays mounted and
          // synced in the background (see loadAndBind/bindVariantSync), so
          // re-entering later is instant, not a fresh load. The frame itself
          // is only ever relying on the photo's z-index to visually cover
          // it here, not display:none - confirmed live that leaving it
          // mounted-and-visible underneath (still actively rendering) can
          // show through as both the photo and the scene at once, so it
          // needs its own explicit hide, same as the photo gets.
          if ( photo && photo.hidden ) {
            photo.hidden = false;
            const frame = container.querySelector('[data-ar-configurator]');
            if (frame) frame.hidden = true;
            btn.textContent = defaultLabel;
            btn.title = btn.dataset.enterLabel || btn.title;
            return;
          }
          // ensureLoaded resolves instantly from cache on a re-entry after
          // an exit above (the scene's already loaded, nothing to redo) -
          // loadAndBind's own photo.hidden only runs on the actual first
          // load, so re-showing it here has to happen unconditionally on
          // every "enter", not just the first one, or a re-entry leaves the
          // photo showing on top of the (already-live) scene underneath.
          const result = await this.ensureLoaded(container, photo, loading);
          if ( !result ) return;
          if (photo) photo.hidden = true;
          if (result.configurator) result.configurator.hidden = false;
          btn.textContent = btn.dataset.exitLabel || defaultLabel;
          if ( btn.dataset.exitLabelTitle ) btn.title = btn.dataset.exitLabelTitle;
        });
        return;
      }

      btn.addEventListener('click', async () => {
        const result = await this.ensureLoaded(container, photo, loading);
        if ( !result ) return;

        const { viewer, configurator, arSourceReady } = result;
        // canActivateAR reflects actual device/browser AR support (WebXR /
        // Scene Viewer / Quick Look) — true on an AR-capable phone, false on
        // desktop. toggleQR() is London Dynamics' own built-in QR box,
        // rendered inside the scene's iframe (already visible — loadAndBind
        // reveals the scene for this button too, same as the 3D button).
        if ( viewer.canActivateAR ) {
          // Only the actual AR launch needs the fetched GLB/USDZ — the QR
          // path below doesn't (scanning it launches AR on a different
          // device entirely), so only this branch waits on it.
          await arSourceReady;
          this.track('ar_launch', container);
          viewer.activateAR();
        } else {
          this.track('ar_qr_open', container);
          // toggleQR() alone opens the panel empty - confirmed live
          // 2026-09-29 it's a separate internal component from the QR image
          // itself, and has nothing to render until getCurrentQR() has been
          // asked for the actual code at least once. A plain one-shot call
          // (not the retry/timeout poll pattern used elsewhere in this file)
          // is safe here: it only ever fires from a direct click, so it's
          // never competing with another getter for the single postMessage
          // response slot the way a background poll would be.
          await new Promise((resolve) => {
            let responded = false;
            configurator.getCurrentQR(() => {
              if (responded) return;
              responded = true;
              resolve();
            });
            setTimeout(() => {
              if (responded) return;
              responded = true;
              resolve();
            }, 3000);
          });
          configurator.toggleQR();
        }
      });
    },

    // Pushed to the same dataLayer convention GTM/most analytics tags read
    // from (auto-created if nothing's loaded it yet, same as GTM's own
    // snippet does, so an event fired before GTM itself loads isn't lost).
    // Only the genuinely deliberate, low-noise signals — a shopper choosing
    // to view the 3D model, launch AR, or open the QR code. Deliberately
    // NOT wiring the SDK's own featureChange/cameraChange analytics events
    // here: every option change already fires one via our own setSelection
    // call, and every camera drag would too, which is a lot of low-value
    // volume for a first pass — worth adding later if it turns out to be
    // wanted, not assumed up front.
    track(eventName, container) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: eventName,
          ar_viewer_sku: container.dataset.arSku || '',
        });
      } catch (e) {}
    },

    // Click-triggered bootstrap for the lazy path: loads the SDK script,
    // creates the <ld-configurator> element (it doesn't exist until now),
    // waits for it to actually respond, syncs the shopper's current
    // selection to it, and reveals it in place of the photo. Returns
    // { configurator, viewer, arSourceReady } for the AR/QR button above.
    async loadAndBind(container, photo, loading) {
      if (photo) photo.hidden = true;
      if (loading) loading.classList.remove('product-gallery-item__ar-loading--hidden');

      await this.loadConfiguratorScript();

      const configurator = document.createElement('ld-configurator');
      configurator.className = 'product-gallery-item__ar-frame';
      configurator.setAttribute('data-ar-configurator', '');
      configurator.setAttribute('customer-id', container.dataset.arCustomerId || '');
      configurator.setAttribute('sku', container.dataset.arSku || '');
      configurator.setAttribute('show-configurator', 'false');
      // The SDK's own native AR button, left off - it renders independently
      // of ours (own iframe, own click handling), can't be styled or
      // suppressed selectively, and confirmed live 2026-09-29 to still be
      // the one actually driving the AR/QR launch when left on, competing
      // with our own Figma-styled button (data-ar-launch-button below).
      configurator.setAttribute('show-ar-button', 'false');
      configurator.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;';

      // Confirmed live 2026-09-29: setSelection can end up called twice in
      // immediate succession with byte-identical content on this lazy path
      // (root cause not pinned down - bindVariantSync's own dedup, keyed on
      // a closure variable, should already prevent this and doesn't) - and
      // when it does, the visual re-texture silently never applies even
      // though getCurrentSelection() keeps correctly echoing the requested
      // state back. A single clean call always rendered correctly in
      // isolated testing regardless of which/how many feature keys it
      // carried. Guarding here, on the instance itself, catches this
      // regardless of which call site is responsible - deliberately a very
      // short window (not a general debounce) so it only ever suppresses a
      // genuine same-tick duplicate, never a real fast double-change.
      let lastSelectionSent = null;
      let lastSelectionSentAt = 0;
      const realSetSelection = configurator.setSelection.bind(configurator);
      configurator.setSelection = (selection, ...rest) => {
        const key = JSON.stringify(selection);
        if (key === lastSelectionSent && Date.now() - lastSelectionSentAt < 1000) return;
        lastSelectionSent = key;
        lastSelectionSentAt = Date.now();
        realSetSelection(selection, ...rest);
      };

      // Listening for 'load' from the moment the element exists, run
      // alongside waitUntilReady below rather than after it resolves - it's
      // a one-shot event with no completion callback to poll instead, so
      // attaching the listener late risks missing it if it fires while
      // waitUntilReady is still working, leaving this stuck on the 8s
      // fallback for no reason.
      const sceneLoaded = this.waitForSceneLoad(configurator);
      container.appendChild(configurator);

      await this.loadModelViewer();
      const viewer = container.querySelector('[data-ar-launch-viewer]');

      // waitUntilReady only confirms the postMessage channel is alive - not
      // that the scene is actually ready to visually apply a setSelection
      // call. Confirmed live: sending a non-default wood-type as the very
      // first selection right after waitUntilReady gets accepted internally
      // (getCurrentSelection correctly echoes it back) but never visually
      // renders — the model keeps showing its original default wood with no
      // error. Waiting for the real 'load' event too (confirmed live to
      // reliably fire, unlike its sibling 'ready' — see the ar-viewer skill)
      // fixes it.
      await Promise.all([this.waitUntilReady(configurator), sceneLoaded]);

      // Kicked off now, in parallel with revealing the scene below — never
      // gates the reveal itself. Confirmed live that a scene can be fully
      // interactive with setSelection working immediately while
      // getCurrentARSource() never resolves at all (e.g. no AR asset for
      // that scene), so the 3D view can't wait on this.
      const arSource = this.startARSourceSync(configurator, viewer);

      let lastSentSelectionKey = null;
      this.bindVariantSync(configurator, container, (selection) => {
        if (!configurator.isConnected) return;
        const selectionKey = JSON.stringify(selection);
        if (selectionKey === lastSentSelectionKey) return;
        lastSentSelectionKey = selectionKey;
        configurator.setSelection(selection, false);
        arSource.refresh();
      });

      // 250ms buffer for the initial setSelection above (fired synchronously
      // by bindVariantSync's own first syncScene() call) to have visually
      // landed before uncovering — setSelection has no completion callback
      // to wait on instead, same reasoning as the eager path below.
      await new Promise((resolve) => setTimeout(resolve, 250));
      if (loading) loading.classList.add('product-gallery-item__ar-loading--hidden');
      this.track('view_in_3d', container);

      // Updated here (not only in the 3D button's own click handler) so the
      // label is right even when this ran without that button ever being
      // clicked — maybeAutoEngage triggers this the same way after a
      // wood-swatch navigation, with no click at all.
      const threeDButton = container.querySelector('[data-ar-3d-button]');
      if (threeDButton) {
        threeDButton.textContent = threeDButton.dataset.exitLabel || threeDButton.textContent;
        if (threeDButton.dataset.exitLabelTitle) threeDButton.title = threeDButton.dataset.exitLabelTitle;
      }

      return { configurator, viewer, arSourceReady: arSource.ready };
    },

    // getCurrentSelection() as a cheap, AR-independent way to confirm the
    // scene is actually alive and answering postMessage calls, before
    // trusting it with setSelection/reveal. Each attempt gets its own
    // timeout — a call made before the iframe's own app has finished
    // booting gets its postMessage dropped with no response, ever (not
    // queued, not errored), and a second call fired regardless of an
    // in-flight one starves both. Gives up (resolves anyway) after
    // maxAttempts rather than leaving a click handler hanging forever.
    waitUntilReady(configurator) {
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 20;
        let inFlight = false;
        const probe = () => {
          if ( inFlight || !configurator.isConnected ) return;
          if ( attempts >= maxAttempts ) { resolve(); return; }
          inFlight = true;
          attempts++;
          let responded = false;
          configurator.getCurrentSelection(() => {
            if (responded) return;
            responded = true;
            inFlight = false;
            resolve();
          });
          setTimeout(() => {
            if (responded) return;
            responded = true;
            inFlight = false;
            probe();
          }, 3000);
        };
        probe();
      });
    },

    // Confirmed live to reliably fire once the 3D scene has actually
    // rendered (distinct from waitUntilReady above, which only confirms the
    // postMessage channel is alive - see loadAndBind's comment on why both
    // are needed). Timed out rather than awaited unconditionally: this
    // integration already found load's sibling event, 'ready', unreliable
    // live on this exact store once before, so nothing here assumes any
    // single SDK event is guaranteed to fire.
    waitForSceneLoad(configurator) {
      return new Promise((resolve) => {
        let settled = false;
        const settle = () => { if (!settled) { settled = true; resolve(); } };
        configurator.addEventListener('load', settle, { once: true });
        setTimeout(settle, 8000);
      });
    },

    // Same one-postMessage-slot-at-a-time retry/timeout pattern as
    // waitUntilReady above. `ready` resolves once (first success, or gives
    // up after maxAttempts so a scene with no AR asset doesn't hang the AR
    // button forever); `refresh` re-triggers the fetch on every later
    // selection change, same as the eager path below. Deliberately doesn't
    // fetch anything until the first refresh() call (from loadAndBind's
    // bindVariantSync callback, right after the first setSelection) rather
    // than starting immediately on creation — confirmed live that firing
    // getCurrentARSource() this close to the scene's very first
    // setSelection call let the AR fetch win the SDK's single postMessage
    // slot, silently stalling that setSelection until it had already been
    // treated as applied (250ms later) and the loading cover lifted — same
    // one-slot constraint documented on fetchARSource below, just not
    // previously known to bite setSelection too.
    startARSourceSync(configurator, viewer) {
      let inFlight = false;
      let attempts = 0;
      const maxAttempts = 20;
      let settled = false;
      let readyResolve;
      const ready = new Promise((resolve) => { readyResolve = resolve; });
      const settle = () => { if (!settled) { settled = true; readyResolve(); } };

      const attempt = () => {
        if ( inFlight || !configurator.isConnected ) return;
        if ( attempts >= maxAttempts ) { settle(); return; }
        inFlight = true;
        attempts++;
        let responded = false;
        configurator.getCurrentARSource((result) => {
          if (responded) return;
          responded = true;
          inFlight = false;
          const { glb, usdz } = result || {};
          if ( !glb ) {
            setTimeout(attempt, 500);
            return;
          }
          attempts = 0;
          viewer.setAttribute('src', glb);
          if (usdz) viewer.setAttribute('ios-src', usdz);
          settle();
        });
        setTimeout(() => {
          if (responded) return;
          responded = true;
          inFlight = false;
          attempt();
        }, 3000);
      };

      return {
        ready,
        refresh: () => { attempts = 0; setTimeout(attempt, 300); },
      };
    },

    // Eager path (see tryBind above) — unchanged from how this integration
    // worked before the photo/3D-toggle pattern existed, for the one case
    // that still loads immediately (no photo to show in its place).
    bindEager(configurator, button, viewer, container, loading) {
      let revealed = false;
      const reveal = () => {
        if ( revealed || !loading ) return;
        revealed = true;
        setTimeout(() => loading.classList.add('product-gallery-item__ar-loading--hidden'), 250);
      };
      setTimeout(reveal, 6000);

      let arFetchInFlight = false;
      let arFetchAttempts = 0;
      const maxArFetchAttempts = 20;

      const fetchARSource = () => {
        if ( arFetchInFlight || arFetchAttempts >= maxArFetchAttempts || !configurator.isConnected ) return;
        arFetchInFlight = true;
        arFetchAttempts++;
        let responded = false;
        configurator.getCurrentARSource((result) => {
          if (responded) return;
          responded = true;
          arFetchInFlight = false;
          const { glb, usdz } = result || {};
          if ( !glb ) {
            setTimeout(fetchARSource, 500);
            return;
          }
          arFetchAttempts = 0;
          viewer.setAttribute('src', glb);
          if (usdz) viewer.setAttribute('ios-src', usdz);
        });
        setTimeout(() => {
          if (responded) return;
          responded = true;
          arFetchInFlight = false;
          fetchARSource();
        }, 3000);
      };

      const refreshARSource = () => {
        arFetchAttempts = 0;
        fetchARSource();
      };

      viewer.addEventListener('load', () => {
        button.hidden = false;
      });

      button.addEventListener('click', async () => {
        if ( viewer.canActivateAR ) {
          viewer.activateAR();
        } else {
          // Same fix as the lazy path's AR/QR handler (bindLazyButton) -
          // toggleQR() alone opens the panel empty, confirmed live
          // 2026-09-29 it has nothing to render until getCurrentQR() has
          // supplied the actual code at least once.
          await new Promise((resolve) => {
            let responded = false;
            configurator.getCurrentQR(() => {
              if (responded) return;
              responded = true;
              resolve();
            });
            setTimeout(() => {
              if (responded) return;
              responded = true;
              resolve();
            }, 3000);
          });
          configurator.toggleQR();
        }
      });

      let configuratorReady = false;
      let pendingSelection = null;
      let probeInFlight = false;
      let probeAttempts = 0;
      const maxProbeAttempts = 20;

      // Both have to be true before anything is trusted with setSelection -
      // the probe alone only confirms the postMessage channel is alive, not
      // that the scene is actually ready to visually apply a selection.
      // Confirmed live: sending a non-default value (e.g. this product's
      // real wood type on a scene whose own default is a different wood) as
      // the very first selection right after the probe succeeds gets
      // accepted internally (getCurrentSelection echoes it back correctly)
      // but never visually renders. Waiting for the real 'load' event too
      // fixes it - timed out rather than awaited unconditionally, same
      // defensive posture as everywhere else here, since this integration
      // already found load's sibling event, 'ready', unreliable live once
      // before.
      let probeSucceeded = false;
      let sceneLoaded = false;
      const tryFlipReady = () => {
        if ( configuratorReady || !probeSucceeded || !sceneLoaded ) return;
        configuratorReady = true;
        if (pendingSelection) {
          configurator.setSelection(pendingSelection, false);
          pendingSelection = null;
        }
        reveal();
        setTimeout(refreshARSource, 300);
      };
      configurator.addEventListener('load', () => { sceneLoaded = true; tryFlipReady(); }, { once: true });
      setTimeout(() => { sceneLoaded = true; tryFlipReady(); }, 8000);

      const probeConfiguratorReady = () => {
        if ( probeSucceeded || probeInFlight || probeAttempts >= maxProbeAttempts || !configurator.isConnected ) return;
        probeInFlight = true;
        probeAttempts++;
        let responded = false;
        configurator.getCurrentSelection(() => {
          if (responded) return;
          responded = true;
          probeInFlight = false;
          probeSucceeded = true;
          tryFlipReady();
        });
        setTimeout(() => {
          if (responded) return;
          responded = true;
          probeInFlight = false;
          probeConfiguratorReady();
        }, 3000);
      };
      probeConfiguratorReady();

      let lastSentSelectionKey = null;
      this.bindVariantSync(configurator, container, (selection) => {
        if (!configurator.isConnected) return;
        if (!configuratorReady) {
          pendingSelection = selection;
          return;
        }
        const selectionKey = JSON.stringify(selection);
        if (selectionKey === lastSentSelectionKey) return;
        lastSentSelectionKey = selectionKey;
        configurator.setSelection(selection, false);
        reveal();
        setTimeout(refreshARSource, 300);
      });
    },

    // Translates Shopify variant/accessory selection into the scene's
    // feature-id/variant-id pairs (see assets/product-3d-configurator-map.js
    // for what's actually confirmed live vs. still assumed) and pushes the
    // result through `sendSelection` whenever either selection surface
    // changes. Two Shopify-side inputs feed the same scene: the product's own
    // variant options (wood type, rail length — via the theme-wide
    // krown:variant:change event) and the metafield-driven accessory drawers
    // (monitor, expandable arm, etc., via pdp_option_drawer's `change` events
    // — see sections/main-product.liquid). accessoryOverrides() below always
    // wins the merge in syncScene(), so a drawer feature with populated
    // metafields reflects the customer's real pick even after a variant
    // change resolves `currentVariant` to a different default for that same
    // feature. Which features are actually wired varies by product and
    // drifts over time — check docs/3d-configurator-integration.md's "Known
    // gaps" section rather than assuming from this comment. Called once the
    // moment the scene becomes available (page load in the eager path, first
    // click in the lazy path) and reads whatever's currently selected at
    // that moment as its first sync — a shopper who already changed options
    // before ever triggering this sees the scene open already correct.
    bindVariantSync(configurator, container, sendSelection) {
      const map = window.ProductConfiguratorMap;
      if ( !map ) return;

      let productOptions = [];
      try {
        productOptions = JSON.parse(container.dataset.productOptions || '[]');
      } catch (error) {
        productOptions = [];
      }

      const modelSelectionFromVariant = (variant) => {
        const selection = {};
        if ( !variant ) return selection;
        productOptions.forEach((option) => {
          const value = variant[`option${option.position}`];
          if ( !value ) return;
          const featureId = map.shopifyOptionToFeature[option.name];
          if ( !featureId ) return;
          const variantId = (map.variantIdByOptionValue[featureId] || {})[String(value).toLowerCase()];
          if ( variantId ) selection[featureId] = variantId;
        });
        return selection;
      };

      // Baseline for features with no Shopify variant option driving them
      // (see accessoryFeatureIds in product-3d-configurator-map.js) — applied
      // BEFORE modelSelectionFromVariant below so a real variant-driven
      // feature (e.g. "monitor", confirmed live to come from the "Performance
      // Technology" option) always wins over this fallback rather than being
      // clobbered by it.
      //
      // 'Frame'/'Logo' (capitalised, Metallic-only per the map file's own
      // comment) are excluded on every other scene. The map file's header
      // comment claims a feature ID a scene doesn't have is always a
      // harmless no-op ("the SDK silently ignores anything outside its own
      // feature list") - confirmed live 2026-09-29 that's not true when it's
      // bundled into the SAME setSelection call as a real change: sending
      // wood-type alongside these two unrecognised keys on the Original
      // scene got wood-type accepted internally (getCurrentSelection echoed
      // it back correctly) but the model never visually re-textured, no
      // error either. Isolating wood-type alone (or with every OTHER real
      // accessory default, just not Frame/Logo) in the same call worked
      // every time. Whatever the SDK's batching logic is doing with an
      // unrecognised key, it's not the harmless no-op the file assumed.
      const accessoryDefaults = () => {
        const selection = {};
        const isMetallic = configurator.getAttribute('sku') === 'waterrower-metallic';
        map.accessoryFeatureIds.forEach((featureId) => {
          if ( (featureId === 'Frame' || featureId === 'Logo') && !isMetallic ) return;
          selection[featureId] = map.defaultVariantByFeature[featureId];
        });
        return selection;
      };

      // wood-frame-1/wood-frame-2 mirror whichever Wood Type just resolved
      // (product decision — they're accent parts, not independently
      // merchandised). Falls back to their own scene default if wood-type
      // didn't resolve to anything (e.g. a product with no Wood Type option).
      //
      // Confirmed live (2026-08-07) that this currently has NO visible effect
      // on the one scene tested: the SDK silently ignores setSelection calls
      // for wood-frame-1/wood-frame-2 entirely — tried alone, in the same
      // call as a working wood-type change, and with valid variant IDs, none
      // of it applied. Consistent with them being absent from that scene's
      // CMS-configured optionGroups (only model/wood-type sit under the
      // "model" group; wood-frame-1/2 and logo appear in the flat
      // options/selection data but aren't part of any optionGroup) — they
      // look like internal/system scene properties, not externally
      // configurable ones. Kept as a harmless no-op rather than removed, in
      // case a different scene's CMS config does expose them as writable.
      const mirrorWoodFrames = (selection) => {
        const { featureIds, variantIdOverridesByWoodType } = map.woodFrameMirrorsWoodType;
        const woodType = selection['wood-type'];
        featureIds.forEach((featureId) => {
          const overrides = variantIdOverridesByWoodType[woodType];
          selection[featureId] = (overrides && overrides[featureId]) || woodType || map.defaultVariantByFeature[featureId];
        });
        return selection;
      };

      // A shopper's actual accessory-drawer pick always wins, applied last.
      // Single-select drawers (pdp_option_drawer with allow_multiple off)
      // show their default product's summary card immediately, but
      // component-pdp-drawers.js only clones the drawer's <template> — and
      // its pre-checked input — into the DOM the first time it's opened. So
      // the visible card is read first as the pre-interaction default, and a
      // real checked input (once the drawer exists) overrides it.
      // Resolves a semantic placeholder (e.g. "bluetooth-monitor") tagged on
      // a shared accessory product to the real variant ID for the scene
      // currently loaded — see variantIdAliasBySku in the map file for why.
      const resolveVariantIdAlias = (variantId) => {
        const alias = map.variantIdAliasBySku[configurator.getAttribute('sku')];
        return (alias && alias[variantId]) || variantId;
      };

      const accessoryOverrides = () => {
        const selection = {};
        // .trim() because these come from free-text admin metafields —
        // confirmed live that a trailing space there silently breaks exact
        // string matching against the scene's real feature/variant IDs.
        document.querySelectorAll('[data-pdp-option-card]:not([hidden])').forEach((card) => {
          const featureId = (card.dataset.ldFeatureId || '').trim();
          const variantId = (card.dataset.ldVariantId || '').trim();
          if ( featureId && variantId ) selection[featureId] = resolveVariantIdAlias(variantId);
        });
        document.querySelectorAll('[data-pdp-option-drawer] input:checked').forEach((input) => {
          const featureId = (input.dataset.ldFeatureId || '').trim();
          const variantId = (input.dataset.ldVariantId || '').trim();
          if ( featureId && variantId ) selection[featureId] = resolveVariantIdAlias(variantId);
        });
        return selection;
      };

      // Evaluates this scene's rules (rulesBySku in the map file) against the
      // given selection. Enforced client-side regardless of whether London
      // Dynamics' own scene config also enforces the same rules server-side
      // (still unconfirmed either way) — see the comment on rulesBySku for
      // why that wouldn't reach our drawer's inputs regardless. Returns the
      // (usually identical) feature ID sets to disable and to fully hide,
      // kept separate to match the sheet's own schema rather than assumed
      // always the same.
      const evaluateBlockedFeatures = (selection) => {
        const disabled = new Set();
        const hidden = new Set();
        const sceneRules = map.rulesBySku[configurator.getAttribute('sku')];
        if ( !sceneRules ) return { disabled, hidden };
        Object.entries(sceneRules.triggers || {}).forEach(([trigger, effect]) => {
          const [featureId, variantId] = trigger.split(':');
          if ( selection[featureId] !== variantId ) return;
          (effect.disable || []).forEach((id) => disabled.add(id));
          (effect.hide || []).forEach((id) => hidden.add(id));
        });
        if ( sceneRules.postProcess ) {
          const result = sceneRules.postProcess(selection) || {};
          (result.disable || []).forEach((id) => disabled.add(id));
          (result.hide || []).forEach((id) => hidden.add(id));
        }
        return { disabled, hidden };
      };

      // Reverts any feature this scene's rules currently block back to its
      // scene default, then re-evaluates — a revert can itself flip another
      // rule's trigger (e.g. on Metallic, reverting Expandable Arm because
      // the monitor changed also changes what the Holder rule sees), so a
      // single evaluate-then-revert pass isn't enough to guarantee the
      // selection actually sent to the scene never violates a rule. Loops to
      // a fixed point instead — confirmed live 2026-08-24 (adversarial code
      // review) that a single pass let a stale, rule-violating value reach
      // setSelection while the drawer UI still correctly showed it blocked.
      // Bounded rather than unconditional in case a future rule set ever
      // creates a cycle with no fixed point; every scene today resolves in
      // at most 2 passes (only expandable-arm and holder are ever targets).
      const resolveRules = (selection) => {
        let blocked = { disabled: new Set(), hidden: new Set() };
        for ( let i = 0; i < 10; i++ ) {
          blocked = evaluateBlockedFeatures(selection);
          const toRevert = [...new Set([...blocked.disabled, ...blocked.hidden])].filter(
            (featureId) => selection[featureId] !== map.defaultVariantByFeature[featureId]
          );
          if ( !toRevert.length ) break;
          toRevert.forEach((featureId) => { selection[featureId] = map.defaultVariantByFeature[featureId]; });
        }
        return blocked;
      };

      // Disables (and visually dims/unchecks) any drawer option belonging to
      // a feature this scene's rules currently block, so a shopper gets a
      // reason instead of the pick just silently reverting — confirmed live
      // it otherwise fails silent. `hide` additionally removes the item from
      // the drawer's layout entirely (see .pdp-option-drawer__item--hidden
      // in section-main-product-pdp.css). Takes the sets resolveRules already
      // settled on rather than re-evaluating independently, so the drawer UI
      // can never disagree with what was actually sent to the scene. In the
      // lazy path this only starts reflecting once the scene has actually
      // been opened at least once — before that there's no scene to violate
      // a rule against, so nothing needs disabling yet.
      const reflectRuleAvailability = (disabled, hidden) => {
        document.querySelectorAll('[data-pdp-option-drawer] input').forEach((input) => {
          const featureId = (input.dataset.ldFeatureId || '').trim();
          if ( !featureId ) return;
          const isDisabled = disabled.has(featureId);
          const isHidden = hidden.has(featureId);
          input.disabled = isDisabled;
          const item = input.closest('.pdp-option-drawer__item');
          if ( item ) {
            item.classList.toggle('pdp-option-drawer__item--disabled', isDisabled);
            item.classList.toggle('pdp-option-drawer__item--hidden', isHidden);
          }
          // Dispatch a real change after unchecking: the drawer's own handler
          // (component-pdp-drawers.js) is what drops the pick from the summary
          // row, price, cart payload and ?upsell= URL, and it only hears a
          // change event - a bare `checked = false` left a now-blocked
          // accessory still listed and still added to the cart.
          if ( (isDisabled || isHidden) && input.checked ) {
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      };

      let currentVariant = null;
      const variantDataScript = container
        .closest('[data-js-product-component]')
        ?.querySelector('[data-js-variant-data]');
      if ( variantDataScript ) {
        try {
          currentVariant = JSON.parse(variantDataScript.textContent);
        } catch (error) {
          currentVariant = null;
        }
      }

      const syncScene = () => {
        const selection = { ...accessoryDefaults(), ...modelSelectionFromVariant(currentVariant) };
        mirrorWoodFrames(selection);
        Object.assign(selection, accessoryOverrides());
        const { disabled, hidden } = resolveRules(selection);
        reflectRuleAvailability(disabled, hidden);
        sendSelection(selection);
      };

      document.addEventListener('krown:variant:change', (event) => {
        currentVariant = event.detail && event.detail.variant;
        syncScene();
      });

      document.addEventListener('change', (event) => {
        if ( event.target.closest('[data-pdp-option-drawer]') ) syncScene();
      });

      // component-pdp-drawers.js only clones a drawer's <template> into the
      // DOM the first time it's opened (appended to document.body) — without
      // this, reflectRuleAvailability/accessoryOverrides would never see
      // that drawer's real inputs until some unrelated change re-ran
      // syncScene. Re-running here as soon as it appears keeps them in sync.
      new MutationObserver(() => syncScene()).observe(document.body, { childList: true });

      syncScene();
    },
  };
}

window.ProductARLauncher.init();
